// Skol-profilöverstyrningar som skolan själv har skickat in via ett godkänt
// anspråk. Uppgifterna publiceras INTE direkt — de landar först i status
// "pending" och blir synliga för besökare först när en verifierare på
// Skolvyn har godkänt uppdateringen. Det säkerställer att kontaktuppgifter
// och statistik alltid är kontrollerade innan de påverkar profilsidan.
import { useEffect, useState } from "react";
import type { TrygghetQA } from "@/components/site/TrygghetStudieroQA";

export type OverrideStatus = "pending" | "published" | "rejected";

export interface SchoolStats {
  /** Antal elever på skolan (senaste läsår). */
  elever?: number;
  /** Andel behöriga lärare i procent (0–100). */
  behorigaLarare?: number;
  /** Genomsnittligt meritvärde åk 9. */
  meritvarde?: number;
  /** Andel elever som känner sig trygga i procent (0–100). */
  trygghet?: number;
  /** Källa (URL) som styrker statistiken, t.ex. skolverket.se-rapport. */
  kallaUrl?: string;
  /** Läsår siffrorna gäller (t.ex. "2024/25"). */
  lasar?: string;
}

export interface SchoolOverride {
  slug: string;
  description?: string;
  phone?: string;
  email?: string;
  website?: string;
  /** Fas 1.1 — skolans egna svar på de fem trygghetsfrågorna. */
  trygghetQA?: TrygghetQA;
  /** Verifierbar statistik som skolan själv har rapporterat. */
  stats?: SchoolStats;

  /** Livscykel: pending = inskickad, väntar granskning. */
  status: OverrideStatus;
  submittedAt: string;
  submittedBy?: string;
  publishedAt?: string;
  verifiedBy?: string;
  rejectedAt?: string;
  rejectedReason?: string;
}

const KEY = "skolvyn:school-overrides";
const EVT = "skolvyn-school-overrides";

// Store: två spår per skola — pending (senaste inskickade) och published (aktiv publik version).
type Store = Record<string, { pending?: SchoolOverride; published?: SchoolOverride }>;

function read(): Store {
  if (typeof window === "undefined") return {};
  try {
    const raw = window.localStorage.getItem(KEY);
    if (!raw) return {};
    const parsed = JSON.parse(raw);
    // Bakåtkompatibilitet: gamla lagrade objekt låg platt per slug och
    // saknade status. Tolka dem som redan publicerade.
    const migrated: Store = {};
    for (const [slug, val] of Object.entries(parsed as Record<string, unknown>)) {
      const v = val as Record<string, unknown>;
      if (v && (v.pending || v.published)) {
        migrated[slug] = v as Store[string];
      } else if (v && typeof v === "object") {
        migrated[slug] = {
          published: {
            slug,
            status: "published",
            submittedAt: (v.updatedAt as string) ?? new Date().toISOString(),
            publishedAt: (v.updatedAt as string) ?? new Date().toISOString(),
            submittedBy: v.updatedBy as string | undefined,
            verifiedBy: "Skolvyn (migrerad)",
            description: v.description as string | undefined,
            phone: v.phone as string | undefined,
            email: v.email as string | undefined,
            website: v.website as string | undefined,
            trygghetQA: v.trygghetQA as TrygghetQA | undefined,
          },
        };
      }
    }
    return migrated;
  } catch {
    return {};
  }
}

function write(store: Store) {
  if (typeof window === "undefined") return;
  window.localStorage.setItem(KEY, JSON.stringify(store));
  window.dispatchEvent(new CustomEvent(EVT));
}

/**
 * Publik läsare — returnerar ENBART den godkända/publicerade versionen.
 * Skolprofilsidan använder denna så att inskickade men ogranskade
 * uppgifter aldrig visas för besökare.
 */
export function getSchoolOverride(slug: string): SchoolOverride | null {
  return read()[slug]?.published ?? null;
}

/** Returnerar senaste inskickade (ännu ogranskade) uppdateringen. */
export function getPendingOverride(slug: string): SchoolOverride | null {
  return read()[slug]?.pending ?? null;
}

/** Alla väntande uppdateringar — används av modereringsvyn. */
export function getAllPendingOverrides(): SchoolOverride[] {
  return Object.values(read())
    .map((s) => s.pending)
    .filter((v): v is SchoolOverride => !!v)
    .sort((a, b) => (a.submittedAt < b.submittedAt ? 1 : -1));
}

/**
 * Skickar in en profiluppdatering för granskning. Skriver ALDRIG till
 * published direkt — versionsspåret pending byts ut med det nya utkastet.
 */
export function submitSchoolOverride(
  slug: string,
  patch: Partial<Omit<SchoolOverride, "slug" | "status" | "submittedAt" | "publishedAt" | "verifiedBy" | "rejectedAt" | "rejectedReason">>,
  submittedBy?: string,
) {
  const store = read();
  const entry = store[slug] ?? {};
  const prev = entry.pending ?? entry.published;
  const next: SchoolOverride = {
    slug,
    status: "pending",
    submittedAt: new Date().toISOString(),
    submittedBy: submittedBy ?? prev?.submittedBy,
    description: patch.description ?? prev?.description,
    phone: patch.phone ?? prev?.phone,
    email: patch.email ?? prev?.email,
    website: patch.website ?? prev?.website,
    trygghetQA: patch.trygghetQA ?? prev?.trygghetQA,
    stats: patch.stats ?? prev?.stats,
  };
  store[slug] = { ...entry, pending: next };
  write(store);
  return next;
}

/** Verifierare godkänner pending → blir ny published version. */
export function publishPendingOverride(slug: string, verifiedBy: string) {
  const store = read();
  const entry = store[slug];
  if (!entry?.pending) return null;
  const published: SchoolOverride = {
    ...entry.pending,
    status: "published",
    publishedAt: new Date().toISOString(),
    verifiedBy,
  };
  store[slug] = { published };
  write(store);
  return published;
}

/** Verifierare avvisar pending — publicerade versionen ligger kvar orörd. */
export function rejectPendingOverride(slug: string, reason: string, verifiedBy: string) {
  const store = read();
  const entry = store[slug];
  if (!entry?.pending) return null;
  const rejected: SchoolOverride = {
    ...entry.pending,
    status: "rejected",
    rejectedAt: new Date().toISOString(),
    rejectedReason: reason,
    verifiedBy,
  };
  store[slug] = { ...entry, pending: undefined };
  // Behåll rejected på en särskild key för spårbarhet
  const historyKey = KEY + ":rejected";
  try {
    const hist = JSON.parse(window.localStorage.getItem(historyKey) ?? "[]");
    hist.unshift(rejected);
    window.localStorage.setItem(historyKey, JSON.stringify(hist.slice(0, 50)));
  } catch {
    /* ignore */
  }
  write(store);
  return rejected;
}

/**
 * Hook för profilsidan — returnerar publicerad version. Denna returnerar
 * ALDRIG en pending uppdatering, så besökare ser bara verifierad data.
 */
export function useSchoolOverride(slug: string | undefined): SchoolOverride | null {
  const [value, setValue] = useState<SchoolOverride | null>(null);
  useEffect(() => {
    if (!slug) return;
    setValue(getSchoolOverride(slug));
    const sync = () => setValue(getSchoolOverride(slug));
    window.addEventListener(EVT, sync);
    window.addEventListener("storage", sync);
    return () => {
      window.removeEventListener(EVT, sync);
      window.removeEventListener("storage", sync);
    };
  }, [slug]);
  return value;
}

/** Hook för hanteringssidan — returnerar både pending och publicerad. */
export function useSchoolOverrideEditor(slug: string | undefined) {
  const [state, setState] = useState<{ pending: SchoolOverride | null; published: SchoolOverride | null }>({
    pending: null,
    published: null,
  });
  useEffect(() => {
    if (!slug) return;
    const load = () => setState({ pending: getPendingOverride(slug), published: getSchoolOverride(slug) });
    load();
    window.addEventListener(EVT, load);
    window.addEventListener("storage", load);
    return () => {
      window.removeEventListener(EVT, load);
      window.removeEventListener("storage", load);
    };
  }, [slug]);
  return state;
}

/** Alla pending — reaktiv hook för modereringsvyn. */
export function usePendingOverrides(): SchoolOverride[] {
  const [list, setList] = useState<SchoolOverride[]>([]);
  useEffect(() => {
    const load = () => setList(getAllPendingOverrides());
    load();
    window.addEventListener(EVT, load);
    window.addEventListener("storage", load);
    return () => {
      window.removeEventListener(EVT, load);
      window.removeEventListener("storage", load);
    };
  }, []);
  return list;
}

/**
 * @deprecated Använd submitSchoolOverride — den nya API:t skickar
 * uppdateringar via en granskningskö istället för direktpublicering.
 */
export function saveSchoolOverride(
  slug: string,
  patch: Partial<Omit<SchoolOverride, "slug" | "status" | "submittedAt" | "publishedAt" | "verifiedBy" | "rejectedAt" | "rejectedReason">>,
  updatedBy?: string,
) {
  return submitSchoolOverride(slug, patch, updatedBy);
}
